import React, { useState, useEffect, useMemo } from 'react';
import { X, UserPlus, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { departmentsApi } from '../../lib/api/departmentsApi';
import { usersApi } from '../../lib/api/usersApi';
import { PERMISSION_GROUP_LABELS, groupPermissionsByModule } from '../../lib/permissionGroups';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';

const MODULES_ORDER = ['job', 'assessment', 'interview', 'department', 'user', 'exam', 'candidate'];

const DUPLICATE_ORG_EMAIL_MESSAGE =
  'This email is already registered to another organization. Raise a ticket from Support for further details.';

function formatUserInviteError(err) {
  const raw = err.response?.data?.message || err.message || 'Request failed';
  if (
    raw === 'A user with this email already exists' ||
    /user with this email already exists/i.test(String(raw))
  ) {
    return DUPLICATE_ORG_EMAIL_MESSAGE;
  }
  return raw;
}

const UserDialog = ({
  open,
  onClose,
  onSuccess,
  user: editingUser,
  defaultDepartmentIds = [],
  initialRole = 'user',
}) => {
  const { user: currentUser } = useAuth();
  useBodyScrollLock(open);
  const isEdit = !!editingUser?._id;
  const isOrgAdmin = currentUser?.accountType === 'org' && currentUser?.role === 'admin';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [departmentIds, setDepartmentIds] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [makeDepartmentAdmin, setMakeDepartmentAdmin] = useState(false);
  const [isInactive, setIsInactive] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');

  // When creating a user from a specific team's "Add users" button,
  // we pass defaultDepartmentIds with exactly one team. Show only that team and lock it.
  const isSingleTeamMode = !isEdit && defaultDepartmentIds && defaultDepartmentIds.length === 1;

  useEffect(() => {
    if (!open) return;
    setName(editingUser?.name || '');
    setEmail(editingUser?.email || '');
    setRole(editingUser?.role || initialRole || 'user');
    if (editingUser) {
      setDepartmentIds((editingUser?.departmentIds || []).map((d) => (d._id || d).toString()));
    } else {
      setDepartmentIds(defaultDepartmentIds.map((id) => id.toString()));
    }
    setSelectedPermissionIds(
      new Set((editingUser?.permissions || []).map((p) => (p._id || p).toString()))
    );
    setMakeDepartmentAdmin(false);
    setIsInactive(editingUser ? editingUser.isActive === false || editingUser.deactivate_account === true : false);
    setError('');
    setLoading(true);
    departmentsApi
      .list()
      .then((data) => setDepartments(data.departments || []))
      .catch(() => setDepartments([]))
      .finally(() => setLoading(false));
  }, [open, editingUser]);

  const selectedDepartments = useMemo(
    () => departments.filter((d) => departmentIds.includes(d._id.toString())),
    [departments, departmentIds]
  );

  const allowedPermissions = useMemo(() => {
    const byId = {};
    selectedDepartments.forEach((d) => {
      (d.permissions || []).forEach((p) => {
        const id = (p._id || p).toString();
        if (!byId[id]) byId[id] = p;
      });
    });
    return Object.values(byId);
  }, [selectedDepartments]);

  const byModule = useMemo(
    () => groupPermissionsByModule(allowedPermissions),
    [allowedPermissions]
  );
  const modulesToShow = MODULES_ORDER.filter((m) => byModule[m]?.length);
  const selectedDeptsWithoutManager = selectedDepartments.filter((d) => d.hasManager === false);
  const showMakeDepartmentAdmin =
    !isEdit && selectedDeptsWithoutManager.length > 0 && role === 'user';

  const toggleDepartment = (deptId) => {
    if (isSingleTeamMode) return;
    const id = deptId.toString();
    setDepartmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const togglePermission = (permId) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  };

  const toggleModule = (moduleName) => {
    const perms = byModule[moduleName] || [];
    const ids = perms.map((p) => p._id.toString());
    const allSelected = ids.length > 0 && ids.every((id) => selectedPermissionIds.has(id));
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const [expandedPermModule, setExpandedPermModule] = useState(null);
  const toggleExpanded = (m) => setExpandedPermModule((prev) => (prev === m ? null : m));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (!isEdit && !email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!isEdit && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email.');
      return;
    }
    setSubmitLoading(true);
    try {
      if (isEdit) {
        await usersApi.update(editingUser._id, {
          name: trimmedName,
          role,
          departmentIds,
          permissions: Array.from(selectedPermissionIds),
          isActive: !isInactive,
        });
      } else {
        await usersApi.create({
          name: trimmedName,
          email: email.trim().toLowerCase(),
          role: makeDepartmentAdmin ? 'department_admin' : role,
          departmentIds,
          permissions: Array.from(selectedPermissionIds),
          makeDepartmentAdmin: showMakeDepartmentAdmin && makeDepartmentAdmin,
        });
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(formatUserInviteError(err));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-xl"
        role="dialog"
        aria-labelledby="user-dialog-title"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-600">
              <UserPlus className="w-5 h-5" />
            </div>
            <h2 id="user-dialog-title" className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit User' : 'New User'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden flex">
          <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Name, Email, Role — compact row */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 transition"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@company.com"
                    disabled={isEdit}
                    className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 disabled:bg-gray-50 disabled:text-gray-500 transition"
                  />
                  {isEdit && <p className="mt-1 text-xs text-gray-400">Email cannot be changed</p>}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 bg-white focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 transition"
                >
                  {isOrgAdmin && (
                    <>
                      <option value="department_admin">Team Admin</option>
                      <option value="user">User</option>
                    </>
                  )}
                  {!isOrgAdmin && <option value="user">User</option>}
                </select>
              </div>
            </div>

            {/* Teams — card section */}
            <div className="rounded-xl border border-gray-200 bg-gray-50/40 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-white/80">
                <h3 className="text-sm font-semibold text-gray-900">Teams</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isSingleTeamMode
                    ? 'User will be added to this team.'
                    : 'Select one or more. User can be a dotted-line manager across teams.'}
                </p>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading teams…
                  </div>
                ) : departments.filter((d) => d.status !== 'inactive').length === 0 ? (
                  <p className="text-sm text-gray-500">No teams available. Create one first.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto">
                    {departments
                      .filter((d) => d.status !== 'inactive')
                      .filter((d) =>
                        isSingleTeamMode
                          ? defaultDepartmentIds
                              .map((id) => id.toString())
                              .includes(d._id.toString())
                          : true
                      )
                      .map((d) => {
                        const id = d._id.toString();
                        const checked = departmentIds.includes(id);
                        return (
                          <label
                            key={d._id}
                            className={`flex ${
                              isSingleTeamMode ? 'cursor-default' : 'cursor-pointer'
                            } items-center gap-3 rounded-lg border px-3 py-2.5 transition ${
                              checked
                                ? 'border-violet-200 bg-violet-50/70'
                                : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDepartment(d._id)}
                              disabled={isSingleTeamMode}
                              className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm font-medium text-gray-800 flex-1 truncate">
                              {d.name}
                            </span>
                            {!isSingleTeamMode && d.hasManager === false && (
                              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                                No admin
                              </span>
                            )}
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            {showMakeDepartmentAdmin && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={makeDepartmentAdmin}
                    onChange={(e) => setMakeDepartmentAdmin(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-amber-900">
                      Make this user the team admin
                    </span>
                    <p className="text-xs text-amber-800/90 mt-0.5">
                      {selectedDeptsWithoutManager.map((d) => d.name).join(', ')} has no admin yet.
                      Assign this user as team admin?
                    </p>
                  </div>
                </label>
              </div>
            )}

            {allowedPermissions.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50/40 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-white/80">
                  <h3 className="text-sm font-semibold text-gray-900">Permissions</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Limited to selected teams. Expand a module to pick specific permissions.
                  </p>
                </div>
                <div className="p-2 max-h-48 overflow-y-auto">
                  {modulesToShow.map((moduleName) => {
                    const perms = byModule[moduleName] || [];
                    const label = PERMISSION_GROUP_LABELS[moduleName] || moduleName;
                    const ids = perms.map((p) => p._id.toString());
                    const allSelected =
                      ids.length > 0 && ids.every((id) => selectedPermissionIds.has(id));
                    const isExp = expandedPermModule === moduleName;
                    return (
                      <div
                        key={moduleName}
                        className="rounded-lg border border-gray-100 bg-white mb-1.5 last:mb-0 overflow-hidden"
                      >
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(moduleName)}
                            className="flex h-8 w-8 shrink-0 items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition"
                            aria-expanded={isExp}
                          >
                            {isExp ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <label className="flex flex-1 cursor-pointer items-center gap-2 py-2 pr-2 min-w-0">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => toggleModule(moduleName)}
                              className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 shrink-0"
                            />
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {label}
                            </span>
                            <span className="text-xs text-gray-400 shrink-0">
                              {ids.filter((id) => selectedPermissionIds.has(id)).length}/
                              {ids.length}
                            </span>
                          </label>
                        </div>
                        {isExp && (
                          <div className="pl-10 pr-3 pb-3 pt-0.5 space-y-1">
                            {perms.map((p) => (
                              <label
                                key={p._id}
                                className="flex cursor-pointer items-center gap-2 rounded-md py-1.5 px-2 hover:bg-gray-50 text-sm text-gray-600"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedPermissionIds.has(p._id.toString())}
                                  onChange={() => togglePermission(p._id.toString())}
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 shrink-0"
                                />
                                <span>{p.description || p.action || p.key}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isEdit && (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isInactive}
                    onChange={(e) => setIsInactive(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Deactivate account</span>
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50/50 px-6 py-4 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-user-btn"
              disabled={submitLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700 focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-70 transition"
            >
              {submitLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create & send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDialog;
